<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { AtomCard, OrgLoadingModal } from "@chatfood/design-system";
import { usePluLoyalty } from "./usePluLoyalty";
import type { PagesOutletProps } from "./props";
import { NuxtRoutedPage } from "~/components/nuxt-routed-page";
import { useBasketStore } from "~/stores/basket";
import { useOutletStore } from "~/stores/outlet";
import { useMenusStore } from "~/stores/menus";
import { useBusinessStore } from "~/stores/business";
import { useRichCard } from "~/composables/use-rich-card";
import { useLocale } from "~/composables/use-locale";
import { useHeaderHeight } from "~/composables/use-header-height";
import { useModalHubStore } from "~/components/modal-hub";
import { useNavigation } from "~/composables/use-navigation";
import { useDeliveryLocationPrompt } from "~/composables/use-delivery-location-prompt";
import { StoreInfo } from "~/components/store-info";
import { BasketView } from "~/components/basket-view";
import { BasketViewCheckout } from "~/components/basket-view-checkout";
import { OrderNotifier } from "~/components/order-notifier";
import { BasketNav } from "~/components/basket-nav";
import { TheMobileHeader } from "~/components/the-mobile-header";
import { OrderingSummaryButton } from "~/components/ordering-summary-button";
import { ItemModal } from "~/modals/item";

const css = bemBuilder("pages-outlet");

const props = defineProps<PagesOutletProps>();

const { t } = useLocale();
const { replace, push, route } = useNavigation();
const { headerHeightPixel } = useHeaderHeight();
const { handlePluLoyalty, onItemHidden, isApplyingLoyalty } = usePluLoyalty();
const { ensureDeliveryLocation } = useDeliveryLocationPrompt();

const business = useBusinessStore();
const outlet = useOutletStore();
const basket = useBasketStore();
const menusStore = useMenusStore();
const modalHub = useModalHubStore();

const checkoutCtaSentinel = ref<HTMLDivElement>();
const isCheckoutCtaSticky = ref(false);
let checkoutCtaObserver: IntersectionObserver | null = null;

const outletClosedMessage = computed(() => {
  if (basket.isForDelivery) {
    return t("pages.outlet.warn_outlet_closed_message_delivery");
  }

  if (basket.isForCurbside) {
    return t("pages.outlet.warn_outlet_closed_message_curbside");
  }

  return t("pages.outlet.warn_outlet_closed_message_pickup");
});

const handleCheckoutUnavailable = () => {
  if (outlet.isClosed) {
    modalHub.showWarning({
      title: t("pages.outlet.warn_outlet_closed_title"),
      message: outletClosedMessage.value,
      dismissLabel: t("pages.outlet.warn_outlet_closed_dismiss"),
      confirmLabel: t("pages.outlet.warn_outlet_closed_confirm"),
      onWarningConfirm: () => {
        replace("order", { orderingMode: basket.orderingMode });
        modalHub.hideWarning();
      },
    });
    return;
  }

  if (
    outlet.orderingMode.outletState === "pre_order_only" &&
    !basket.scheduledTime
  ) {
    modalHub.showOrderForLaterWarn();
    return;
  }

  ensureDeliveryLocation();
};

const handleMobileCheckoutSticky = () => {
  if (!checkoutCtaSentinel.value) return;

  checkoutCtaObserver = new IntersectionObserver((entries) => {
    if (entries[0].intersectionRatio <= 0) {
      isCheckoutCtaSticky.value = true;
    } else {
      isCheckoutCtaSticky.value = false;
    }
  });

  checkoutCtaObserver.observe(checkoutCtaSentinel.value);
};

const defineSeoHeader = () => {
  const title = t("pages.outlet.seo_title", {
    outletName: outlet.name,
    businessName: business.name,
  });

  const description = t("pages.outlet.seo_description", {
    businessName: business.name,
  });

  const image = business.cover || business.logo;

  useRichCard({ title, description, image });
};

watch(
  () => route.value.query.itemId,
  (value) => {
    if (value) return;
    defineSeoHeader();
  },
  { immediate: true },
);

defineSeoHeader();

onMounted(() => {
  handleMobileCheckoutSticky();
  handleCheckoutUnavailable();
  handlePluLoyalty();
});

onUnmounted(() => {
  if (checkoutCtaObserver && checkoutCtaSentinel.value) {
    checkoutCtaObserver.unobserve(checkoutCtaSentinel.value);
    checkoutCtaObserver.disconnect();
    checkoutCtaObserver = null;
  }
});

const mobileTitle = computed(() => {
  if (!props.menuSlug) return t("pages.outlet.header_title");

  return (
    menusStore.menus.find((menu) => menu.slug === props.menuSlug)?.name ||
    t("pages.outlet.header_title")
  );
});

const onBack = () => {
  if (route.value.name === "outlet" || menusStore.menus.length <= 1) {
    push("order", {});
  } else {
    push("outlet", {
      orderingMode: props.orderingMode,
      outletSlug: props.outletSlug,
    });
  }
};

const activeMenuId = computed(() => {
  /**
   * @TODO: remove
   * `menuId` is available for every item from menus pushed from november/2025
   * onwards. For older items, we fallback to the menuSlug prop to determine.
   * We can remove this logic in few months from this date.
   */
  if (menusStore.menus.length === 1) return menusStore.firstMenu.id;

  return (
    menusStore.menus.find((menu) => menu.slug === props.menuSlug)?.id || ""
  );
});
</script>

<template>
  <div :class="css()">
    <ClientOnly v-if="!business.isMobileApp">
      <Teleport to="#nav-append">
        <BasketNav />
      </Teleport>
    </ClientOnly>
    <div :class="css('content')">
      <div :class="css('left-pane')">
        <div v-if="business.isMobileApp">
          <TheMobileHeader
            no-shadow
            hide-actions
            :title="mobileTitle"
            :cta-action="onBack"
          />
          <div :class="css('order-mode-mobile')">
            <OrderingSummaryButton />
          </div>
        </div>
        <StoreInfo v-else :ordering-mode="props.orderingMode" />
        <NuxtRoutedPage />
      </div>
      <div :class="css('right-pane')">
        <AtomCard padding="none" :class="css('sticky-basket')">
          <BasketView />
        </AtomCard>
      </div>
    </div>
    <div
      :class="{
        [css('order-notifier')]: true,
        [css('order-notifier', 'further')]: basket.items.length,
      }"
    >
      <OrderNotifier />
    </div>
    <div
      v-if="basket.items.length"
      :class="{
        [css('checkout-cta')]: true,
        [css('checkout-cta', 'sticky')]: isCheckoutCtaSticky,
      }"
    >
      <BasketViewCheckout />
    </div>
    <div ref="checkoutCtaSentinel"></div>
    <ItemModal :menu-id="activeMenuId" @hidden="onItemHidden" />
    <OrgLoadingModal
      v-model="isApplyingLoyalty"
      data-test="loading-loyalty"
      :primary-text="t('pages.outlet.plu_loyalty.loading_modal_primary')"
      :secondary-text="t('pages.outlet.plu_loyalty.loading_modal_secondary')"
    />
  </div>
</template>

<style lang="scss" scoped>
$calc-header-and-sub-header-height: v-bind(headerHeightPixel);

.pages-outlet {
  width: 100%;
  display: flex;
  flex-direction: column;

  @media (width >= $breakpoint-sm) {
    padding: var(--size-lg);
  }

  @media (width >= $breakpoint-lg) {
    padding: var(--spacing-2xl);
  }

  @media (width <= $breakpoint-sm) {
    &__order-mode-mobile {
      padding-inline: var(--spacing-md);
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-2xl);
    margin-bottom: var(--spacing-2xl);
    margin-inline: auto;
    width: 1484px;
    max-width: 100%;

    @media (width >= $breakpoint-lg) {
      display: grid;
      flex-direction: unset;
      grid-template-columns: minmax(300px, 1fr) minmax(300px, 400px);
    }
  }

  &__left-pane {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg);
  }

  &__right-pane {
    display: none;

    @media (width >= $breakpoint-sm) {
      display: unset;
    }
  }

  &__sticky-basket {
    @media (width >= $breakpoint-lg) {
      position: sticky;
      top: calc($calc-header-and-sub-header-height + var(--spacing-2xl));
    }
  }

  &__order-notifier {
    max-width: 100%;
    position: sticky;
    bottom: var(--spacing-md);
    z-index: 2;
    text-align: center;
    padding-inline: var(--spacing-md);

    @media (width < $breakpoint-sm) {
      &--further {
        bottom: 80px;
      }
    }

    @media (width > $breakpoint-md) {
      display: inline-block;
      width: 360px;
      left: 50%;
      padding-inline: 0;
      transform: translate(-50%, 0);
    }
  }

  &__checkout-cta {
    z-index: 2;
    position: sticky;
    bottom: 0;
    padding: var(--spacing-md);
    background-color: var(--color-base-100);

    &--sticky {
      border-top: 1px solid var(--color-base-95);
    }

    @media (width > $breakpoint-sm) {
      display: none;
    }
  }
}
</style>
